import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Logic, StructureItemValueType, StructureItemType } from '../type/query-transform.enum';
import type {
    StructureChildren,
    StructureItemValue,
    StructureItem,
    TransformedQueryItem,
    FeatureBiddingItem,
} from '../type/query-transform.type';
import type { ElementItem, ElementsState } from '../type/ai-search.type';
import { KeywordType } from '../type/ai-search.enum';
import { Symbols } from '@/constants';
import type VueI18n from 'vue-i18n';

export const QueryTransformService = Symbol('QueryTransformService');

export interface QueryTransformService {
    transformQuery: (elements: ElementsState) => TransformedQueryItem[];
}

const FIELD_MAP: Record<string, StructureItemType> = {
    TOPIC: StructureItemType.TA_ALL,
    FEATURE: StructureItemType.TAC_All,
    EFFECT: StructureItemType.DESC_All,
};

@injectable()
export class QueryTransformServiceImpl implements QueryTransformService {
    constructor(
        @inject(Symbols.VueI18n)
        private _i18n: VueI18n,
    ) {
    }

    // 基础表达式
    private _outputList: TransformedQueryItem[] = [];
    // 是否有topic
    private _hasTopic: boolean = true;

    private _appendStructureLogic(target: StructureItemValue[], logic: Logic) {
        return [...target, { type: StructureItemValueType.LOGIC, value: logic }];
    }

    // 处理word，如果有空格则用引号包裹
    private _handleWord(word: string): string {
        return word.includes(' ')
            ? `"${ word }"`
            : word;
    }

    private _handleSingleExtend(keywords: ElementItem['keywords']): StructureItemValue[] {
        // 单个 extend
        const keywordValue = keywords?.[0]?.value;

        if (keywords.length === 0 || !keywordValue || keywordValue?.length === 0) {
            return [];
        }

        let cacheList: StructureItemValue[] = [];

        keywordValue.forEach((word, idx) => {
            cacheList.push({
                type: StructureItemValueType.FIELD,
                value: this._handleWord(word),
            });

            const noLastItem = idx < keywordValue.length - 1;
            if (noLastItem) {
                cacheList = this._appendStructureLogic(cacheList, Logic.OR);
            }
        });

        return cacheList;
    }

    private _handleMultiExtend(keywords: ElementItem['keywords']): StructureItemValue[] {
        // 多个 extend
        const isSomeValue = keywords.some(item => item.value?.length > 0);
        if (!isSomeValue) return [];

        const cacheList: StructureItemValue[] = [];

        const extendGroup: StructureChildren = {
            type: StructureItemValueType.GROUP,
            children: [],
        };

        keywords.forEach((item, idx) => {
            if (item.value.length === 0) return;

            const extendChildGroup: StructureChildren = {
                type: StructureItemValueType.GROUP,
                children: [],
            };

            item.value?.forEach((word, extendWordIdx: number) => {
                extendChildGroup.children.push({
                    type: StructureItemValueType.FIELD,
                    value: this._handleWord(word),
                });

                if (item.value.length - 1 > extendWordIdx) {
                    extendChildGroup.children = this._appendStructureLogic(extendChildGroup.children, Logic.OR);
                }
            });

            extendGroup.children.push(extendChildGroup);

            if (keywords.length - 1 > idx && (keywords[idx + 1]?.value?.length ?? 0) > 0) {
                extendGroup.children = this._appendStructureLogic(extendGroup.children, Logic.AND);
            }
        });

        cacheList.push(extendGroup);

        return cacheList;
    }

    private _getStructureData(ele: ElementItem): StructureItem {
        const { type, feature } = ele;
        const keywords = ele.keywords.filter(item => item.value.length > 0);
        const value: StructureItemValue[] = keywords.length > 1
            ? this._handleMultiExtend(keywords)
            : this._handleSingleExtend(keywords);

        return {
            type: FIELD_MAP[type],
            value,
            feature,
        };
    }

    private _getStructureIPCData(ipc: { code: string; id: string; }[]): StructureItem {
        let value: StructureItemValue[] = [];
        ipc.forEach((item, idx) => {
            value.push({
                type: StructureItemValueType.FIELD,
                value: this._handleWord(item.code),
            });

            if (idx < ipc.length - 1) {
                value = this._appendStructureLogic(value, Logic.OR);
            }
        });

        return {
            type: StructureItemType.IPC,
            value,
        };
    }

    private _getOutputSemanticData(semantic: { value: string; id: string; }): TransformedQueryItem {
        return {
            html: `${ this._getFieldAttrWrap('Semantic') }: (${ semantic.value })`,
            string: `[SEMANTIC]${ semantic.id }`,
        };
    }

    private _handleMultiLevelGroup(snCycleIdxList: number[], snFixedIdxList: number[], desc: string): TransformedQueryItem[] {
        snCycleIdxList = snCycleIdxList.filter(idx => idx >= 0);
        snFixedIdxList = snFixedIdxList.filter(idx => idx >= 0);
        if (snFixedIdxList.length === 0 || snCycleIdxList.length === 0) return [];

        const targetList = snCycleIdxList.map(snIdx => {
            return [snIdx, ...snFixedIdxList].sort((a, b) => a - b).filter(idx => idx >= 0);
        }).filter(arr => arr.length > 1);

        return targetList.map(snArr => {
            const snList = snArr.map(snIdx => `S${ snIdx + 1 }`);
            const wrappedSnList = snArr.map(snIdx => `${ this._getFieldAttrWrap('S' + (snIdx + 1)) }`);
            const stringList = snArr.map(snIdx => `${ this._outputList[snIdx].string }`);

            return {
                desc,
                isGroup: true,
                html: wrappedSnList.join(` ${ this._getLogicAttrWrap('AND') } `),
                string: stringList.join(' AND '),
                codeQuery: snList.join(' AND '),
            };
        });
    }

    private _getFieldAttrWrap(content: string): string {
        return `<span data-query-transform="field">${ content }</span>`;
    }

    private _getLogicAttrWrap(content: string): string {
        return `<span data-query-transform="logic">${ content }</span>`;
    }

    private _getLogicContent(content: string): TransformedQueryItem {
        return {
            html: this._getLogicAttrWrap(content),
            string: content,
        };
    }

    private _handleJoin(queryItem: (TransformedQueryItem | TransformedQueryItem[])[], key: string, noBrackets?: boolean): string {
        let strCache = '';
        const itemLength = queryItem.length;
        queryItem.forEach((item, idx) => {
            if (Array.isArray(item)) {
                return strCache += item.length > 1 && !noBrackets
                    ? `(${ this._handleJoin(item, key) })`
                    : this._handleJoin(item, key);
            } else {
                strCache += itemLength - 1 > idx && idx !== 0
                    ? ` ${ item[key] } `
                    : item[key];
            }
        });

        return strCache;
    }

    private _getContentBody(itemValue: StructureItemValue[]): (TransformedQueryItem | TransformedQueryItem[])[] {
        return itemValue.map(item => {
            if (item.children) {
                return this._getContentBody(item.children) as TransformedQueryItem[];
            }

            return item.type === StructureItemValueType.LOGIC
                ? this._getLogicContent(item?.value ?? '')
                : {
                    html: `${ item.value }`,
                    string: `${ item.value }`,
                };
        });
    }

    private _transform2QueryItem(sItem: StructureItem): TransformedQueryItem {
        const contentBody = this._getContentBody(sItem.value);
        const htmlContentBody = this._handleJoin(contentBody, 'html', true);
        const stringContentBody = this._handleJoin(contentBody, 'string', true);

        return {
            html: `${ this._getFieldAttrWrap(sItem.type) }:(${ htmlContentBody })`,
            string: `${ sItem.type }:(${ stringContentBody })`,
        };
    }

    private _getFeatureBiddingMap(sList: StructureItem[]): FeatureBiddingItem[] {
        return sList.reduce((acc, cur, idx) => {
            cur.feature?.forEach(featWord => {
                const accIdx = acc.findIndex(item => item?.featWord === featWord);

                if (accIdx === -1) {
                    acc.push({ featWord, bidding: [idx] });
                } else {
                    acc[accIdx].bidding.push(idx);
                }
            });

            return acc;
        }, [] as FeatureBiddingItem[]);
    }

    private _getStructureListAndBidding(state: ElementsState) {
        const { topic, element } = state;
        // 结构化； semantic 直接输出，不需要结构化
        // 结构化 topic
        const topicElement = topic.elements[0];
        this._hasTopic = topicElement && topicElement.keywords?.[0]?.value?.length > 0;
        const sList: StructureItem[] = [];
        if (this._hasTopic) {
            const structureTopic = this._getStructureData(topicElement);
            sList.push(structureTopic);
        }

        // 去除拓展词为空的元素
        const elements = element.elements.filter(ele => ele.keywords?.find(item => item?.value?.length > 0));

        // 结构化 特征词
        const featureList = elements
            .filter(ele => ele.type === KeywordType.FEATURE)
            .map(ele => this._getStructureData(ele));

        sList.push(...featureList);

        // 结构化 效果词
        const effectList = elements
            .filter(ele => ele.type === KeywordType.EFFECT)
            .map(ele => this._getStructureData(ele));

        const effectBidding: number[] = [];
        effectList.forEach(item => {
            effectBidding.push(sList.length);
            sList.push(item);
        });

        // 取特征点下标map
        const featureBiddingMap = this._getFeatureBiddingMap(sList);

        // 结构化 ipc
        const ipcList = [
            topic.relatedIpc,
            element.relatedIpc,
        ].map(item => this._getStructureIPCData(item));

        const ipcBidding: number[] = [];
        ipcList.forEach(item => {
            if (item.value.length > 0) {
                ipcBidding.push(sList.length);
                sList.push(item);
            } else {
                ipcBidding.push(-1);
            }
        });

        return { sList, ipcBidding, featureBiddingMap, effectBidding };
    }

    private _connectOutput(state: ElementsState) {
        const { sList, ipcBidding, featureBiddingMap, effectBidding } = this._getStructureListAndBidding(state);

        // Sn （ta,tac,desc,ipc） 输出
        this._outputList = sList.map(s => this._transform2QueryItem(s));

        // semantic 输出
        const semanticOutputList = state.semantic?.map(ele => this._getOutputSemanticData(ele)) ?? [];

        this._outputList.push(...semanticOutputList);

        const topicName = state.topic?.elements?.[0]?.element;

        // 1. 组合输出 topic & ipc（topic)
        if (ipcBidding[0] !== -1 && this._hasTopic) {
            const s1IpcOutput = this._handleMultiLevelGroup([0], ipcBidding.slice(0, 1), topicName);
            this._outputList.push(...s1IpcOutput);
        }

        // 2. 组合输出 特征点 & ipc(element) 
        // 3. 组合输出 特征点 & topic
        const ipcTopicIdxList = [
            ipcBidding[1],
            this._hasTopic
                ? 0
                : -1,
        ];
        featureBiddingMap.forEach((featItem: FeatureBiddingItem) => {
            const featOutput = this._handleMultiLevelGroup(ipcTopicIdxList, featItem.bidding, featItem.featWord);
            this._outputList.push(...featOutput);
        });

        // 4. 组合输出: 效果词 循环 and topic 和 ipc（topic）
        const topicIpcIdxList = ipcBidding[0] === -1
            ? [0]
            : [0, ipcBidding[0]];
        const effectDesc = this._i18n.t('aiSearch.strategyEffectDesc', { topicName }) as string;
        const effectS1IpcOutput = this._handleMultiLevelGroup(effectBidding, topicIpcIdxList, effectDesc);
        this._outputList.push(...effectS1IpcOutput);

    }

    transformQuery(state: ElementsState): TransformedQueryItem[] {
        this._outputList = [];

        // 组合输出
        this._connectOutput(state);

        return this._outputList;
    }
}
